import { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import { Button } from "../../../components/ui/button";
import DataTable from "../../../components/ui/data-table/data-table";
import { createActionsColumn } from "../../../components/ui/data-table/columns";
import { SearchInput } from "../../../components/ui/search-input";
import staticBannerService from "../../../services/StaticBannerService";
import highlightOrderService from "services/HighlightOrderService";
import LocationDropdown from "components/LocationDropdown/LocationDropdown";
import { FormDialog } from "../../../components/ui/form-dialog";
import CommonFormNew from "../../../components/commonFormNew/commonFormNew";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { dateFormate } from "utils/utils";
import { Edit2Icon, Trash2, PlusIcon, X } from "lucide-react";
import { toast, ToastContainer } from 'react-toastify';
import { NewPagination } from "../../../components/ui/newPagination";
import dayjs  from "dayjs";

export default function StaticBanner() {
  const [bannerData, setBannerData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isAddBannerOpen, setIsAddBannerOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingBannerId, setEditingBannerId] = useState(null);
  const [addToHighlightBannerId, setAddToHighlightBannerId] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    image: null,
    dateRange: { startDate: null, endDate: null },
    city: null,
    duration: { startDate: null, endDate: null }, // Added for highlight form
    location: null, // Added for highlight form
  });
  const [selectedImage, setSelectedImage] = useState(null);

const rawCities = useSelector((state) => state?.locationDropdown?.locations || []);

const cityOptions = [
  { id: "all", name: "All City" },
  ...rawCities
];


  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Fixed form validation function
  function isFormValid(form, fields) {
    return fields.every((field) => {
      if (!field.required) return true;

      const fieldValue = form[field.name];

      switch (field.type) {
        case 'input':
          return fieldValue && fieldValue.trim() !== "";
        case 'image':
          return fieldValue !== null && fieldValue !== undefined;
        case 'dateRange':
          return fieldValue && fieldValue.startDate && fieldValue.endDate && (Array.isArray(form?.city) ? form?.city.length > 0 : form?.city !== null);
        default:
          return fieldValue !== null && fieldValue !== undefined && fieldValue !== "";
      }
    });
  }

  const fetchBanners = async (search, page, limit) => {
    try {
      let query = {};
      if (search) {
        query.search = encodeURIComponent(search.trim());
      }
      query.page = page || 1;
      query.limit = limit || 10;
      const res = await staticBannerService.getBannerList(query);
      const headers = res?.data?.header || [];
      const body = res?.data?.body || [];
      setTotalItems(res?.data?.totalCount || 0);

      const dynamicColumns = headers.map(({ key, label }) => {
        if (key === "image") {
          return {
            accessorKey: key,
            header: label,
            cell: ({ row }) => {
              const img = row.original.image;
              return (
                <div onClick={(e) => e.stopPropagation()}>
                  {img && img.url ? (
                    <img
                      src={img.url}
                      alt={img.name}
                      style={{ width: 60, height: 40, objectFit: "cover", borderRadius: 4, cursor: "pointer" }}
                      onClick={() => setSelectedImage(img.url)}
                    />
                  ) : (
                    <span className="text-gray-400">No Image</span>
                  )}
                </div>
              );
            },
          };
        }
        return {
          accessorKey: key,
          header: label,
        };
      });

      const actionsColumn = {
        accessorKey: "actions",
        header: "Action",
        cell: ({ row }) =>
          createActionsColumn([
            {
              label: "Add to highlight",
              onClick: () => {
                setAddToHighlightBannerId(row.original.id);
                // Reset form data for highlight form
                setFormData({
                  title: "",
                  image: null,
                  dateRange: { startDate: null, endDate: null },
                  city: null,
                  duration: { startDate: null, endDate: null },
                  location: null,
                });
                setIsAddBannerOpen(true);
              },
              icon: <PlusIcon></PlusIcon>
            },
            {
              label: "Edit",
              onClick: () => {
                setFormData({
                  title: row.original.title,
                  image: row.original.image,
                  dateRange: row.original.dateRange || { startDate: null, endDate: null },
                  city: row.original.city || null,
                  duration: { startDate: null, endDate: null },
                  location: null,
                });
                setEditingBannerId(row.original.id);
                setIsEditing(true);
                setIsAddBannerOpen(true);
              },
              icon: <Edit2Icon></Edit2Icon>
            },
            {
              label: "Delete",
              onClick: async () => {
                try {
                  await staticBannerService.deleteBanner(row.original.id);
                  toast.success(`Banner '${row.original.title}' deleted successfully`);
                  fetchBanners(search);
                } catch (error) {
                  toast.error("Failed to delete banner");
                }
              },
              icon: <Trash2></Trash2>
            },
          ]).cell({ row }),
      };

      setBannerData(body);
      setColumns([...dynamicColumns, actionsColumn]);
      setFilteredData(body);
    } catch (error) {
      console.error("Error fetching banners:", error);
    }
  };

  const handleAddBanner = async () => {
    try {
      const payload = {
        title: formData.title,
        bannerImage: formData.image,
        city: formData.city,
        dateRange: {
          startDate: formData.dateRange?.startDate
            ? dateFormate(formData.dateRange.startDate, "MM/DD/YYYY")
            : "",
          endDate: formData.dateRange?.endDate
            ? dateFormate(formData.dateRange.endDate, "MM/DD/YYYY")
            : "",
        },
      };

      if (isEditing && editingBannerId) {
        await staticBannerService.updateBanner(editingBannerId, payload);
        toast.success("Banner updated successfully");
      } else {
        await staticBannerService.createBanner(payload);
        toast.success("Banner added successfully");
      }

      setFormData({ 
        title: "", 
        image: null, 
        dateRange: { startDate: null, endDate: null }, 
        city: null,
        duration: { startDate: null, endDate: null },
        location: null,
      });
      setIsEditing(false);
      setEditingBannerId(null);
      setIsAddBannerOpen(false);
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.message || (isEditing ? "Failed to update banner" : "Failed to add banner"));
    }
  };

  const handleAddToHighLight = async () => {
    try {
      const payload = {
        type: "banner",
        referenceId: addToHighlightBannerId,
        locations: Array.isArray(formData.city) 
          ? formData.city.map((itm) => itm.id) 
          : [formData.city?.id].filter(Boolean),
        startDate: formData.dateRange?.startDate
          ? dateFormate(formData.dateRange.startDate, "toIsoString")
          : "",
        endDate: formData.dateRange?.endDate
          ? dateFormate(formData.dateRange.endDate, "toIsoString")
          : ""
      };

      await highlightOrderService.addToHighLight(payload);
      toast.success("Banner added to highlight successfully");
      
      setFormData({ 
        title: "", 
        image: null, 
        dateRange: { startDate: null, endDate: null }, 
        city: null,
        duration: { startDate: null, endDate: null },
        location: null,
      });
      setIsEditing(false);
      setEditingBannerId(null);
      setIsAddBannerOpen(false);
      setAddToHighlightBannerId(null);
      fetchBanners();
    } catch (error) {
      console.log('err', error, error.response?.data)
      toast.error(error.response?.data?.message || "Failed to add banner to highlight");
    }
  };

  const handleCancel = () => {
    setFormData({ 
      title: "", 
      image: null, 
      dateRange: { startDate: null, endDate: null }, 
      city: null,
      duration: { startDate: null, endDate: null },
      location: null,
    });
    setIsEditing(false);
    setEditingBannerId(null);
    setIsAddBannerOpen(false);
    setAddToHighlightBannerId(null);
  };

  // Debounced search
  const debouncedFetchBanners = useCallback(
    debounce((query) => {
      fetchBanners(query);
    }, 500),
    []
  );

  const handleSearch = (query) => {
    setSearchQuery(query || "");
    debouncedFetchBanners(query);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchBanners(null, page, itemsPerPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchBanners(null, 1, newItemsPerPage);
  };

  useEffect(() => {
    // Call fetchBanners with current page and items per page
    fetchBanners(searchQuery, currentPage, itemsPerPage);
  }, [currentPage, itemsPerPage, searchQuery]);

  useEffect(() => {
    return () => {
      debouncedFetchBanners.cancel();
    };
  }, [debouncedFetchBanners]);

  // Updated field configurations
  const bannerFields = [
    { name: "title", label: "Name", type: "input", required: true, placeholder: "Enter Name" },
    { name: "image", label: "", type: "image", required: true }
  ];

  const addToHighlightFields = [
    { name: "dateRange", label: "Duration", type: "dateRange", required: true, placeholder: "Duration",minDate: dayjs().add(1, "day") },
    { name: "city", label: "City", type: "city", required: true, placeholder: "City" }
  ];

  // Determine current fields and form validity
  const currentFields = addToHighlightBannerId ? addToHighlightFields : bannerFields;
  const isValid = isFormValid(formData, currentFields);

  return (
    <div className="mx-auto">
      <div className="mb-4 flex justify-end">
        <div className="flex items-center gap-2">
          <SearchInput onSearch={handleSearch} placeholder={"Banner Name"} className="w-[280px]" />
          
          <FormDialog
            trigger={
              <Button className="h-[40px] bg-[#ffa726] hover:bg-[#ffa726] rounded-[5px] font-normal text-[14px] text-white border border-white capitalize">
                + Add Banner
              </Button>
            }
            title={isEditing ? "Edit" : "Add"}
            subtitle={addToHighlightBannerId ? "To Highlight" : "Banner"}
            isOpen={isAddBannerOpen}
            onOpenChange={setIsAddBannerOpen}
            onSubmit={addToHighlightBannerId ? handleAddToHighLight : handleAddBanner}
            onCancel={handleCancel}
            submitLabel={isEditing ? "Update" : "Create"}
            submitDisabled={!isValid}
            hideCancel={true}
          > 
           <LocalizationProvider dateAdapter={AdapterDayjs}>
            {addToHighlightBannerId && <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-2">
              Highlights must be configured at least one day in advance. Please select tomorrow or a later date.
            </p>}
            <CommonFormNew
              config={currentFields}
              formData={formData}
              onFormDataChange={setFormData}
              cityOptions={cityOptions}
            />
           </LocalizationProvider>
          </FormDialog>
        </div>
      </div>
      <div>
        <DataTable
          columns={columns}
          data={filteredData}
          onRowClick={(row) => toast({ title: "Row Clicked", description: `You clicked ${row.toll}` })}
          pageSize={itemsPerPage}
        />
        <NewPagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>
      <ToastContainer />

      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="relative bg-white p-4 rounded-lg w-[800px] h-[600px] flex items-center justify-center"
            onClick={e => e.stopPropagation()}
          >
            <img
              src={selectedImage}
              alt="Preview"
              className="max-w-full max-h-full object-contain"
            />
            <button
              className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-md hover:bg-gray-100"
              onClick={() => setSelectedImage(null)}
            >
              <X size={24} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
};
